import moment from 'moment';
import { get, isEmpty } from 'lodash';
import Axios from 'axios';

//common actions import

import {
  beginAjaxCallAction,
  endAjaxCallAction,
  apiErrorResponseAction,
  dispatchCurrentTimeStampAction,
} from '../common';

import {
  SIGN_IN_SUCCESS,
  SIGNUP_RESTAURANT,
  PSEUDO_SIGN_IN,
  SUPER_ADMIN_SIGN_IN_SUCCESS,
} from './actionTypes';
import {
  initiateSignInApi,
  verifyRestaurantUserName,
  signUpRestaurantApi,
  initiatePseudoSignInApi,
  initiateSuperAdminSignInApi,
} from '../../api';
import { logoutApi, logoutAllDevicesApi } from '../../api/authSession';
import { readFudrAdminData } from '../../utils/helpers';

export { refreshTokenAction, updateAuthTokensAction } from '../authTokens';

export const initiateSignInAction =
  ({ signInReqBody }) =>
  async (dispatch) => {
    let userObjKey = '';
    let timeStamp = moment().format('MM/DD/YYYY HH:mm:ss');
    let assistanceTimeStamp = moment().format('MM/DD/YYYY HH:mm:ss');
    let fudrAdminData = readFudrAdminData();
    try {
      dispatch(beginAjaxCallAction());
      const initiateSignInAPIResponse = await initiateSignInApi(signInReqBody)
        .then((response) => {
          delete Axios.defaults.headers['Authorization'];
          Axios.defaults.headers.Authorization = `Bearer ${response?.data?.accessToken}`;
          const data = get(response, 'data', {});
          userObjKey = data.user && data.user.name;
          if (!isEmpty(fudrAdminData)) {
            if (fudrAdminData.hasOwnProperty(userObjKey)) {
              if (!isEmpty(fudrAdminData[userObjKey].currentTimeStamp)) {
                timeStamp = fudrAdminData[userObjKey].currentTimeStamp;
              }
              if (!isEmpty(fudrAdminData[userObjKey].assistanceTimeStamp)) {
                assistanceTimeStamp = fudrAdminData[userObjKey].assistanceTimeStamp;
              }
            }
          } else {
            fudrAdminData = {};
          }
          fudrAdminData[userObjKey] = {
            currentTimeStamp: timeStamp,
            assistanceTimeStamp: assistanceTimeStamp,
            authData: data,
          };
          dispatch(dispatchCurrentTimeStampAction({ timeStamp, assistanceTimeStamp }));
          dispatch({
            type: SIGN_IN_SUCCESS,
            authData: data,
          });
          localStorage.setItem('fudrAdminData', JSON.stringify(fudrAdminData));
          return response;
        })
        .catch((errorResponse) => {
          dispatch(apiErrorResponseAction(get(errorResponse, 'response.data', {})));
          return get(errorResponse, 'response', {});
        });
      return initiateSignInAPIResponse;
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(endAjaxCallAction());
    }
  };

export const dispatchLoginDataAction =
  ({ authData }) =>
  async (dispatch) => {
    dispatch({
      type: SIGN_IN_SUCCESS,
      authData,
    });
  };

const logSilentAuthFailure = (op, error) => {
  const correlationId = get(error, 'response.headers.x-correlation-id', '');
  const status = get(error, 'response.status', '');
  console.error(`[auth:${op}] failed`, { status, correlationId });
};

const getAccessTokenForLogout = (getState) => {
  const state = getState();
  return (
    get(state, 'restaurant.authData.accessToken', '') ||
    get(state, 'authentication.authData.accessToken', '') ||
    get(state, 'fudr.authData.accessToken', '')
  );
};

/**
 * Resolves the bearer token for logout calls.
 * Callers may pass accessToken from props (snapshot) so the value is fixed before any
 * synchronous clearSignInData dispatches run. With axios@0.21, request interceptors on
 * shared clients can run after the current stack; pairing this with await in clearSignInData
 * keeps server-side revocation reliable even if logout ever goes through such a client.
 */
const resolveAccessTokenForLogout = (opts, getState) =>
  get(opts, 'accessToken', '') || getAccessTokenForLogout(getState);

export const logoutCurrentDeviceSilentlyAction =
  (opts = {}) =>
  async (_dispatch, getState) => {
    try {
      await logoutApi(resolveAccessTokenForLogout(opts, getState));
    } catch (e) {
      logSilentAuthFailure('logout', e);
    }
  };

export const logoutAllDevicesSilentlyAction =
  (opts = {}) =>
  async (_dispatch, getState) => {
    try {
      await logoutAllDevicesApi(resolveAccessTokenForLogout(opts, getState));
    } catch (e) {
      logSilentAuthFailure('logoutAllDevices', e);
    }
  };

export const clearAuthenticationDataAction =
  ({ signedInUsername }) =>
  async (dispatch) => {
    const fudrAdminData = readFudrAdminData();
    if (signedInUsername && fudrAdminData[signedInUsername]?.authData) {
      delete fudrAdminData[signedInUsername].authData;
    }
    dispatch({
      type: SIGN_IN_SUCCESS,
      authData: {},
    });
    try {
      localStorage.setItem('fudrAdminData', JSON.stringify(fudrAdminData));
    } catch {
      // quota / private mode — session clear in Redux still applies
    }
  };

export const verifyRestaurantUserNameAction =
  ({ username }) =>
  async (dispatch) => {
    try {
      dispatch(beginAjaxCallAction());
      const verifyRestaurantUserNameResponse = await verifyRestaurantUserName({ username })
        .then((response) => get(response, 'data', {}))
        .catch((errorResponse) => {
          dispatch(apiErrorResponseAction(get(errorResponse, 'response.data', {})));
          return get(errorResponse, 'response', {});
        });
      return verifyRestaurantUserNameResponse;
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(endAjaxCallAction());
    }
  };

export const signUpRestaurantAction =
  ({ signUpRestaurantReqBody }) =>
  async (dispatch) => {
    try {
      dispatch(beginAjaxCallAction());
      const signUpRestaurantResponse = await signUpRestaurantApi({ signUpRestaurantReqBody })
        .then((response) => {
          dispatch({
            type: SIGNUP_RESTAURANT,
            signUpRestaurantData: get(response, 'data', {}),
          });
          return response;
        })
        .catch((errorResponse) => {
          dispatch(apiErrorResponseAction(get(errorResponse, 'response.data', {})));
          return get(errorResponse, 'response', {});
        });

      return signUpRestaurantResponse;
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(endAjaxCallAction());
    }
  };

export const initiatePseudoSignInAction =
  ({ signInReqBody, history }) =>
  async (dispatch) => {
    let userObjKey = '';
    let timeStamp = moment().format('MM/DD/YYYY HH:mm:ss');
    let assistanceTimeStamp = moment().format('MM/DD/YYYY HH:mm:ss');
    let fudrAdminData = readFudrAdminData();

    try {
      dispatch(beginAjaxCallAction());
      const initiatePseudoSignInAPIResponse = await initiatePseudoSignInApi(signInReqBody)
        .then((response) => {
          delete Axios.defaults.headers['Authorization'];
          Axios.defaults.headers.Authorization = `Bearer ${response?.data?.accessToken}`;
          const data = get(response, 'data', {});
          userObjKey = data.user && data.user.name;

          if (!isEmpty(fudrAdminData)) {
            if (fudrAdminData.hasOwnProperty(userObjKey)) {
              if (!isEmpty(fudrAdminData[userObjKey].currentTimeStamp)) {
                timeStamp = fudrAdminData[userObjKey].currentTimeStamp;
              }
              if (!isEmpty(fudrAdminData[userObjKey].assistanceTimeStamp)) {
                assistanceTimeStamp = fudrAdminData[userObjKey].assistanceTimeStamp;
              }
            }
          } else {
            fudrAdminData = {};
          }

          fudrAdminData[userObjKey] = {
            currentTimeStamp: timeStamp,
            assistanceTimeStamp: assistanceTimeStamp,
            authData: data,
          };

          dispatch(dispatchCurrentTimeStampAction({ timeStamp, assistanceTimeStamp }));

          dispatch({
            type: SIGN_IN_SUCCESS,
            authData: data,
          });

          localStorage.setItem('fudrAdminData', JSON.stringify(fudrAdminData));

          return response;
        })
        .catch((errorResponse) => {
          dispatch(apiErrorResponseAction(get(errorResponse, 'response.data', {})));
          return get(errorResponse, 'response', {});
        });

      return initiatePseudoSignInAPIResponse;
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(endAjaxCallAction());
    }
  };

export const dispatchIsPseudoSignInAction = (isPseudoSignIn) => async (dispatch) => {
  dispatch({
    type: PSEUDO_SIGN_IN,
    isPseudoSignIn,
  });
};

export const initiateSuperAdminSignInAction =
  ({ signInReqBody }) =>
  async (dispatch) => {
    // let userObjKey = '';
    // let timeStamp = moment().format('MM/DD/YYYY HH:mm:ss');
    // let assistanceTimeStamp = moment().format('MM/DD/YYYY HH:mm:ss');
    // let superAdminData = JSON.parse(localStorage.getItem('superAdminData'));

    try {
      dispatch(beginAjaxCallAction());

      const initiateSuperAdminSignInApiResponse = await initiateSuperAdminSignInApi(signInReqBody)
        .then((response) => {
          delete Axios.defaults.headers['Authorization'];
          // set accessToken in defaulr headers
          Axios.defaults.headers.Authorization = `Bearer ${response?.data?.accessToken}`;

          const data = response?.data ?? {};
          // userObjKey = data?.user?.name;

          // if (!isEmpty(superAdminData)) {
          //   if (superAdminData.hasOwnProperty(userObjKey)) {
          //     if (!isEmpty(superAdminData[userObjKey].currentTimeStamp)) {
          //       timeStamp = superAdminData[userObjKey].currentTimeStamp;
          //     }
          //     if (!isEmpty(superAdminData[userObjKey].assistanceTimeStamp)) {
          //       assistanceTimeStamp = superAdminData[userObjKey].assistanceTimeStamp;
          //     }
          //   }
          // } else {
          //   superAdminData = {};
          // }

          // superAdminData[userObjKey] = {
          //   // currentTimeStamp: timeStamp,
          //   // assistanceTimeStamp: assistanceTimeStamp,
          //   authData: data,
          // };
          // dispatch(dispatchCurrentTimeStampAction({ timeStamp, assistanceTimeStamp }));

          dispatch({
            type: SUPER_ADMIN_SIGN_IN_SUCCESS,
            authData: data,
          });

          // localStorage.setItem('superAdminData', JSON.stringify(superAdminData));

          return response;
        })
        .catch((errorResponse) => {
          dispatch(apiErrorResponseAction(get(errorResponse, 'response.data', {})));
          return get(errorResponse, 'response', {});
        });

      return initiateSuperAdminSignInApiResponse;
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(endAjaxCallAction());
    }
  };

export const dispatchSuperAdminSignInDataAction =
  ({ authData }) =>
  async (dispatch) => {
    dispatch({
      type: SUPER_ADMIN_SIGN_IN_SUCCESS,
      authData,
    });
  };

export const clearSuperAdminAuthDataAction =
  ({ signedInUsername }) =>
  async (dispatch) => {
    // let superAdminData = JSON.parse(localStorage.getItem('superAdminData'));
    // superAdminData &&
    //   superAdminData[signedInUsername] &&
    //   superAdminData[signedInUsername]['authData'] &&
    //   delete superAdminData[signedInUsername]['authData'];

    dispatch({
      type: SUPER_ADMIN_SIGN_IN_SUCCESS,
      authData: {},
    });

    // localStorage.setItem('superAdminData', JSON.stringify(superAdminData));
  };
